const TX_TYPES = new Set(['sale', 'order', 'transfer']);
const TX_STATUSES = new Set(['pending_deposit', 'pending_delivery', 'delivered', 'cancelled', 'pending']);

export function validateTransactionPayload(tx) {
  const errors = [];
  if (!tx || typeof tx !== 'object') {
    return ['Datos de la transacción ausentes'];
  }

  const type = tx.type || tx.transaction_type;
  if (!TX_TYPES.has(type)) {
    errors.push('Tipo de operación inválido');
  }

  const amount = Number(tx.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    errors.push('El monto debe ser mayor a cero');
  }

  if (!tx.branchId && !tx.branch_id) {
    errors.push('Debe especificarse la sucursal');
  }

  if (type === 'sale' && !(tx.clientId || tx.client_id)) {
    errors.push('Las ventas requieren un cliente');
  }

  if (type === 'order' && !(tx.supplierId || tx.supplier_id)) {
    errors.push('Los pedidos requieren un proveedor');
  }

  if (type === 'transfer' && !(tx.targetBranchId || tx.target_branch_id)) {
    errors.push('Las transferencias internas requieren una sucursal destino');
  }

  if (type === 'transfer' && String(tx.branchId ?? tx.branch_id ?? '') === String(tx.targetBranchId ?? tx.target_branch_id ?? '')) {
    errors.push('La sucursal destino debe ser diferente a la sucursal origen');
  }

  if (type === 'order' || type === 'transfer') {
    if (tx.costAmount != null) {
      const cost = Number(tx.costAmount);
      if (!Number.isFinite(cost) || cost < 0) {
        errors.push('Los costos no pueden ser negativos');
      }
    }
    if (tx.purchasePercent != null) {
      const pct = Number(tx.purchasePercent);
      if (!Number.isFinite(pct) || pct < 0) {
        errors.push('El % de compra no puede ser negativo');
      }
    }
    if (Array.isArray(tx.orderCharges)) {
      for (const [idx, ch] of tx.orderCharges.entries()) {
        const t = ch?.type;
        const v = Number(ch?.value);
        const m = ch?.mode;
        if (t !== 'percent' && t !== 'flat') {
          errors.push(`Cargo ${idx + 1}: tipo inválido`);
        }
        if (!Number.isFinite(v) || v <= 0) {
          errors.push(`Cargo ${idx + 1}: valor debe ser mayor a cero`);
        }
        if (m !== 'on_top' && m !== 'from_total') {
          errors.push(`Cargo ${idx + 1}: modo inválido`);
        }
      }
    }
  }

  if (tx.status && !TX_STATUSES.has(tx.status)) {
    errors.push('Estado de operación desconocido');
  }

  const brokerCommission = tx.brokerCommission ?? tx.broker_commission;
  if (brokerCommission != null) {
    const commission = Number(brokerCommission);
    if (Number.isNaN(commission) || commission < 0) {
      errors.push('La comisión del broker no puede ser negativa');
    }
  }

  return errors;
}

export function assertValidTransaction(tx) {
  const errors = validateTransactionPayload(tx);
  if (errors.length > 0) {
    const error = new Error('Transacción inválida');
    error.cause = errors;
    throw error;
  }
}

// Generic validators
export function validateNonEmptyString(value, label) {
  if (typeof value !== 'string' || value.trim() === '') return `${label} es requerido`;
  return undefined;
}

export function validateNonNegativeNumber(value, label) {
  if (value == null) return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return `${label} no puede ser negativo`;
  return undefined;
}

export function validateOptionalPercent(value, label = 'Porcentaje') {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return `${label} inválido`;
  return undefined;
}

// Domain validators
export function validateClient(input) {
  const errors = [];
  const eName = validateNonEmptyString(input?.name ?? input?.company_name, 'Nombre del cliente');
  if (eName) errors.push(eName);
  const ePct = validateOptionalPercent(input?.defaultSalePercentage ?? input?.default_sale_percentage, 'Porcentaje de venta');
  if (ePct) errors.push(ePct);
  return errors;
}

export function validateSupplier(input) {
  const errors = [];
  const eName = validateNonEmptyString(input?.name, 'Nombre del proveedor');
  if (eName) errors.push(eName);
  const eFee = validateNonNegativeNumber(input?.feeRate ?? input?.fee_rate, 'Porcentaje de proveedor');
  if (eFee) errors.push(eFee);
  const eCost = validateNonNegativeNumber(input?.defaultCostAmount ?? input?.default_cost_amount, 'Costo por defecto');
  if (eCost) errors.push(eCost);
  const ePurchase = validateNonNegativeNumber(input?.defaultPurchasePercent ?? input?.default_purchase_percent, '% de compra por defecto');
  if (ePurchase) errors.push(ePurchase);
  if (Array.isArray(input?.defaultOrderCharges)) {
    for (const [idx, ch] of input.defaultOrderCharges.entries()) {
      const t = ch?.type;
      const v = Number(ch?.value);
      const m = ch?.mode;
      if (t !== 'percent' && t !== 'flat') errors.push(`Cargo ${idx + 1}: tipo inválido`);
      if (!Number.isFinite(v) || v <= 0) errors.push(`Cargo ${idx + 1}: valor debe ser mayor a cero`);
      if (m !== 'on_top' && m !== 'from_total') errors.push(`Cargo ${idx + 1}: modo inválido`);
    }
  }
  return errors;
}

export function validateBroker(input) {
  const errors = [];
  const eName = validateNonEmptyString(input?.name, 'Nombre del broker');
  if (eName) errors.push(eName);
  const eRate = validateNonNegativeNumber(input?.commissionRate ?? input?.commission_rate, 'Comisión del broker');
  if (eRate) errors.push(eRate);
  return errors;
}

export function validateBranch(input) {
  const errors = [];
  const eName = validateNonEmptyString(input?.name, 'Nombre de la sucursal');
  if (eName) errors.push(eName);
  const eInit = validateNonNegativeNumber(input?.initialCash ?? input?.initial_cash, 'Caja inicial');
  if (eInit) errors.push(eInit);
  const eCash = validateNonNegativeNumber(input?.cashBalance ?? input?.cash_balance, 'Saldo en caja');
  if (eCash) errors.push(eCash);
  return errors;
}

export function validateAccount(input) {
  const errors = [];
  const eName = validateNonEmptyString(input?.name, 'Nombre de la cuenta');
  if (eName) errors.push(eName);
  return errors;
}
